import { ChangeEvent, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { SaleDepositResponse } from "../../types/sale-deposit.type";
import { formatBusinessDate } from "../SaleDepositReport/saleCenterClosing.utils";

/**
 * Bounded so an over-long reason is refused in the field rather than as a 400 from the
 * server, matching the review notes elsewhere in the app.
 */
const REASON_MAX_LENGTH = 255;

const EMPTY_REASON_ERROR = "กรุณาระบุเหตุผลที่ต้องการแก้ไข";

interface EditRequestDialogProps {
  open: boolean;
  /** The one deposit being asked about — a route on a business day. */
  deposit: SaleDepositResponse | null;
  saving: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * Raising คำขอแก้ไขยอดโอน for one deposit.
 *
 * The reason is the entire content of the request: the supervisor approves or refuses on it
 * alone, and a refusal is usually "this did not say enough". So a blank or whitespace-only
 * reason cannot be sent, and the dialog names who will read it — the branch is not filing a
 * form, it is asking a person.
 */
export default function EditRequestDialog({
  open,
  deposit,
  saving,
  onClose,
  onConfirm,
}: EditRequestDialogProps) {
  const [reason, setReason] = useState<string>("");
  // Held back until the first submit so an untouched field is not already scolding.
  const [showEmptyError, setShowEmptyError] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setReason("");
      setShowEmptyError(false);
    }
  }, [open]);

  const handleChangeReason = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setReason(event.target.value);
    setShowEmptyError(false);
  };

  const handleConfirm = () => {
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setShowEmptyError(true);
      return;
    }
    onConfirm(trimmedReason);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>ขอแก้ไขยอดโอน</DialogTitle>
      <DialogContent dividers>
        <Stack gap={2}>
          <Alert severity="info">
            คำขอนี้จะถูกส่งไปยังหัวหน้าบัญชีสำนักงานใหญ่เพื่อพิจารณา
            เมื่ออนุมัติแล้วระบบจะเปิดให้แก้ไขรายการนี้อีกครั้ง
            และล้างสถานะตรวจแล้วของรายการนี้
          </Alert>
          {deposit && (
            <Typography variant="body2" color="text.secondary">
              สายส่ง {deposit.route_code} · สาขา {deposit.branch_code || "-"} ·
              วันที่ทำรายการ {formatBusinessDate(deposit.business_date)}
            </Typography>
          )}
          <TextField
            label="เหตุผลที่ขอแก้ไข"
            placeholder="ระบุสิ่งที่ต้องแก้ไข และเหตุผลให้ชัดเจน"
            value={reason}
            onChange={handleChangeReason}
            fullWidth
            required
            multiline
            rows={3}
            size="small"
            error={showEmptyError}
            slotProps={{ htmlInput: { maxLength: REASON_MAX_LENGTH } }}
            helperText={
              showEmptyError
                ? EMPTY_REASON_ERROR
                : `${reason.length}/${REASON_MAX_LENGTH}`
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          ยกเลิก
        </Button>
        <Button onClick={handleConfirm} variant="contained" loading={saving}>
          ส่งคำขอ
        </Button>
      </DialogActions>
    </Dialog>
  );
}
